import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Play, CheckCircle2, XCircle, AlertTriangle, RotateCcw, Loader2, Search, Trash2 } from "lucide-react";

const STAGES = [
  { key: "mapping", label: "1. Map columns" },
  { key: "preview", label: "2. Preview & resolve" },
  { key: "commit", label: "3. Commit" },
  { key: "summary", label: "4. Summary" },
];

const FIELD_LABEL = {
  external_id: "External / CRM ID",
  customer_ref: "Customer ref (email, phone, or external id)",
  invoice_ref: "Invoice ref (external id or number)",
  name: "Name",
  email: "Email",
  phone: "Phone",
  address: "Address",
  notes: "Notes",
  source: "Source",
  status: "Status",
  value: "Value",
  service_type: "Service type",
  title: "Title / service",
  number: "Number",
  subtotal: "Subtotal",
  tax: "Tax",
  total: "Total",
  amount_paid: "Amount paid",
  balance: "Balance",
  amount: "Amount",
  method: "Method",
  created_at: "Created date",
  dated_at: "Dated / estimate date",
  due_at: "Due date",
  sent_at: "Sent date",
  paid_at: "Paid date",
  scheduled_start: "Scheduled start",
  scheduled_end: "Scheduled end",
};

export default function ImportWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [rowFilter, setRowFilter] = useState("all");
  const [defaultDup, setDefaultDup] = useState("skip");

  const load = async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        api.get(`/imports/${id}`),
        api.get(`/imports/${id}/rows`),
      ]);
      setBatch(b.data);
      setRows(r.data);
      setMapping(b.data.mapping || {});
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const currentStage = useMemo(() => {
    if (!batch) return 0;
    if (batch.status === "committed" || batch.status === "rolled_back") return 3;
    if (batch.status === "previewed") return 2;
    if (batch.status === "mapped") return 1;
    return 0;
  }, [batch]);

  const saveMapping = async () => {
    setBusy(true);
    try {
      await api.put(`/imports/${id}/mapping`, { mapping });
      toast.success("Mapping saved");
      await load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      await api.put(`/imports/${id}/mapping`, { mapping });
      const { data } = await api.post(`/imports/${id}/preview`);
      toast.success(`Preview: ${data.stats.new} new · ${data.stats.duplicates} dup · ${data.stats.errors} errors`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const setRowAction = async (row, action) => {
    try {
      await api.patch(`/imports/${id}/rows/${row.id}`, { action });
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, action } : r));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const commit = async () => {
    if (!window.confirm(`Commit this import? Duplicates will be ${defaultDup === "skip" ? "skipped" : defaultDup === "merge" ? "merged" : "created anyway"}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${id}/commit`, { default_action_for_duplicates: defaultDup });
      toast.success(`Imported ${data.stats.imported} · Skipped ${data.stats.skipped} · Merged ${data.stats.merged} · Failed ${data.stats.failed}`, { duration: 8000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Commit failed"); }
    finally { setBusy(false); }
  };

  const rollback = async () => {
    if (!window.confirm("Roll back this import? All newly-created records will be deleted. Merged records will keep the incoming data but lose the import marker.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${id}/rollback`);
      toast.success(`Rolled back — removed ${JSON.stringify(data.removed)}`, { duration: 8000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline"/></div>;
  if (!batch) return <div className="p-8 text-zinc-500">Batch not found.</div>;

  const filteredRows = rows.filter((r) => {
    if (rowFilter === "duplicates") return (r.dup_status || "new") !== "new";
    if (rowFilter === "errors") return (r.validation_errors || []).length > 0;
    return true;
  });

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-6">
        <Link to="/app/imports" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> All imports</Link>
      </div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">{batch.entity_type} import</div>
          <h1 className="font-heading text-3xl font-bold truncate">{batch.filename}</h1>
          <div className="text-sm text-zinc-500 mt-1 font-mono-accent">batch · {batch.id} · {batch.total_rows} rows · status {batch.status}</div>
        </div>
        <div className="flex items-center gap-2">
          {batch.status === "committed" && (
            <Button onClick={rollback} disabled={busy} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="import-rollback-btn">
              <RotateCcw className="w-4 h-4 mr-1"/> Roll back
            </Button>
          )}
        </div>
      </div>

      {/* Stage stepper */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {STAGES.map((s, i) => (
          <div key={s.key} className={`px-3 py-1.5 rounded-md text-xs font-mono-accent uppercase tracking-widest border ${i === currentStage ? "bg-[#FF5A00] text-white border-[#FF5A00]" : i < currentStage ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-zinc-400 border-zinc-200"}`}>
            {s.label}
          </div>
        ))}
      </div>

      {/* Stage 1: Mapping */}
      {batch.status !== "committed" && batch.status !== "rolled_back" && (
        <div className="bg-white border border-zinc-200 rounded-xl card-crisp mb-6" data-testid="import-mapping-panel">
          <div className="p-6 border-b border-zinc-100">
            <div className="font-heading font-bold text-lg">Column mapping</div>
            <p className="text-sm text-zinc-500">Map each LeadForge field to a column in your file. Auto-suggested mappings shown — adjust as needed.</p>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(batch.target_fields || []).map((field) => (
              <div key={field} className="flex items-center gap-3">
                <div className="min-w-[180px]">
                  <div className="text-sm font-medium">{FIELD_LABEL[field] || field}</div>
                  <div className="text-[11px] text-zinc-400 font-mono-accent">{field}</div>
                </div>
                <Select
                  value={mapping[field] || "__none__"}
                  onValueChange={(v)=>setMapping({...mapping, [field]: v === "__none__" ? "" : v})}
                >
                  <SelectTrigger className="flex-1" data-testid={`import-map-${field}`}><SelectValue placeholder="(not mapped)"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(not mapped)</SelectItem>
                    {(batch.headers || []).map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="p-6 border-t border-zinc-100 flex items-center justify-between">
            <div className="text-xs text-zinc-500">{Object.values(mapping).filter(Boolean).length} of {(batch.target_fields || []).length} fields mapped</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={saveMapping} disabled={busy} data-testid="import-save-mapping-btn">Save mapping</Button>
              <Button onClick={runPreview} disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="import-preview-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Play className="w-4 h-4 mr-1"/>}
                Preview
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview stats + rows */}
      {batch.stats && batch.status !== "uploaded" && batch.status !== "mapped" && (
        <div className="bg-white border border-zinc-200 rounded-xl card-crisp mb-6">
          <div className="p-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading font-bold text-lg">Preview & rows</div>
              <div className="text-sm text-zinc-500 mt-1">
                Total <b>{batch.stats.total || rows.length}</b> ·
                New <b className="text-emerald-600">{batch.stats.new ?? 0}</b> ·
                Duplicates <b className="text-amber-600">{batch.stats.duplicates ?? 0}</b> ·
                Errors <b className="text-rose-600">{batch.stats.errors ?? 0}</b>
                {batch.status === "committed" && (<>
                  {" · "}Imported <b>{batch.stats.imported ?? 0}</b> · Skipped <b>{batch.stats.skipped ?? 0}</b> · Merged <b>{batch.stats.merged ?? 0}</b> · Failed <b>{batch.stats.failed ?? 0}</b>
                </>)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={rowFilter} onValueChange={setRowFilter}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="import-row-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All rows</SelectItem>
                  <SelectItem value="duplicates">Duplicates only</SelectItem>
                  <SelectItem value="errors">Errors only</SelectItem>
                </SelectContent>
              </Select>
              {batch.status === "previewed" && (
                <>
                  <Select value={defaultDup} onValueChange={setDefaultDup}>
                    <SelectTrigger className="w-40 h-8 text-xs" data-testid="import-default-dup"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="skip">Skip duplicates</SelectItem>
                      <SelectItem value="merge">Merge duplicates</SelectItem>
                      <SelectItem value="create">Create new anyway</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={commit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-commit-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <CheckCircle2 className="w-4 h-4 mr-1"/>}
                    Commit
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-50 border-b border-zinc-100 text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
                <tr>
                  <th className="p-2 text-left w-10">#</th>
                  <th className="p-2 text-left">Preview values</th>
                  <th className="p-2 text-left w-40">Status</th>
                  <th className="p-2 text-left w-40">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filteredRows.map((r) => (
                  <tr key={r.id} data-testid={`import-row-${r.id}`} className={
                    r.validation_errors?.length ? "bg-rose-50/40" :
                    r.dup_status !== "new" ? "bg-amber-50/40" : ""
                  }>
                    <td className="p-2 font-mono-accent text-xs text-zinc-500">{r.row_index + 1}</td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                        {Object.entries(r.staged || r.raw).slice(0, 6).map(([k, v]) => (
                          <span key={k} className="text-zinc-700"><span className="text-zinc-400">{k}:</span> {String(v).slice(0, 40)}</span>
                        ))}
                      </div>
                      {r.validation_errors?.length > 0 && (
                        <div className="text-xs text-rose-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>{r.validation_errors.join(" · ")}</div>
                      )}
                    </td>
                    <td className="p-2">
                      {r.dup_status === "new" ? (
                        <span className="text-xs text-emerald-700 font-mono-accent uppercase">new</span>
                      ) : (
                        <span className="text-xs text-amber-700 font-mono-accent uppercase">{(r.dup_status || "").replace(/_/g, " ")}</span>
                      )}
                    </td>
                    <td className="p-2">
                      {batch.status === "committed" ? (
                        <span className="text-xs text-zinc-500 font-mono-accent uppercase">{r.committed ? "created" : "skipped"}</span>
                      ) : batch.status === "previewed" ? (
                        <Select value={r.action || "create"} onValueChange={(v)=>setRowAction(r, v)}>
                          <SelectTrigger className="h-7 text-xs w-32" data-testid={`import-row-action-${r.id}`}><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="create">Create</SelectItem>
                            <SelectItem value="skip">Skip</SelectItem>
                            {r.dup_status !== "new" && r.match_target_id && <SelectItem value="merge">Merge</SelectItem>}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-zinc-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {batch.status === "committed" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6" data-testid="import-summary">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0"/>
            <div>
              <div className="font-heading font-bold text-lg">Import committed</div>
              <div className="text-sm text-emerald-800 mt-1">Records have been added to your LeadForge workspace. They're marked as historical — no notifications, emails, SMS, or follow-up automations were triggered.</div>
              <div className="text-sm text-emerald-800 mt-3 font-mono-accent">
                Imported {batch.stats.imported} · Skipped {batch.stats.skipped} · Merged {batch.stats.merged} · Failed {batch.stats.failed}
              </div>
            </div>
          </div>
        </div>
      )}

      {batch.status === "rolled_back" && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-6" data-testid="import-rollback-summary">
          <div className="flex items-start gap-3">
            <RotateCcw className="w-6 h-6 text-rose-600 shrink-0"/>
            <div>
              <div className="font-heading font-bold text-lg">Import rolled back</div>
              <div className="text-sm text-rose-800 mt-1">All records created by this import have been deleted. You can start a new import from the Imports page.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
